import { useEffect } from 'react';
import './FleurCannonAnimation.css';

export default function FleurCannonAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.closest('.battle-container') || document.body;
    const cRect = container.getBoundingClientRect();

    const pRect = playerEl.getBoundingClientRect();
    const oRect = opponentEl.getBoundingClientRect();

    const startX = Math.round((pRect.left + pRect.width / 2) - cRect.left);
    const startY = Math.round((pRect.top + pRect.height / 2) - cRect.top);
    const endX = Math.round((oRect.left + oRect.width / 2) - cRect.left);
    const endY = Math.round((oRect.top + oRect.height / 2) - cRect.top);

    const dx = endX - startX;
    const dy = endY - startY;
    const angleRad = Math.atan2(dy, dx);
    const distance = Math.hypot(dx, dy);

    const elements = [];

    playerEl.classList.add('fleur-cannon-user-charge');

    const sigil = document.createElement('div');
    sigil.className = 'fleur-cannon-sigil';
    sigil.style.left = `${startX - 90}px`;
    sigil.style.top = `${startY - 90}px`;
    sigil.style.width = '180px';
    sigil.style.height = '180px';
    container.appendChild(sigil);
    elements.push(sigil);

    const aura = document.createElement('div');
    aura.className = 'fleur-cannon-charge-aura';
    aura.style.left = `${startX - 110}px`;
    aura.style.top = `${startY - 110}px`;
    aura.style.width = '220px';
    aura.style.height = '220px';
    container.appendChild(aura);
    elements.push(aura);

    for (let i = 0; i < 10; i++) {
      const petal = document.createElement('div');
      petal.className = 'fleur-cannon-orbit-petal';
      const a = (i / 10) * 360;
      const r = 80 + (i % 2) * 12;
      const x = startX + Math.cos(a * Math.PI / 180) * r;
      const y = startY + Math.sin(a * Math.PI / 180) * r;
      petal.style.left = `${x - 10}px`;
      petal.style.top = `${y - 10}px`;
      petal.style.animationDelay = `${i * 40}ms`;
      petal.textContent = '🌸';
      container.appendChild(petal);
      elements.push(petal);
    }

    const phase2Timer = setTimeout(() => {
      playerEl.classList.remove('fleur-cannon-user-charge');
      playerEl.classList.add('fleur-cannon-user-fire');

      for (let i = 0; i < 7; i++) {
        const pulse = document.createElement('div');
        pulse.className = 'fleur-cannon-beam-pulse';
        const t = (i + 1) / 8;
        const x = startX + dx * t;
        const y = startY + dy * t;
        pulse.style.left = `${x}px`;
        pulse.style.top = `${y}px`;
        pulse.style.animationDelay = `${i * 60}ms`;
        container.appendChild(pulse);
        elements.push(pulse);
      }
    }, 700);
    elements.push({ timer: phase2Timer, type: 'timer' });

    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('fleur-cannon-opponent-hit');

      const burst = document.createElement('div');
      burst.className = 'fleur-cannon-impact-burst';
      burst.style.left = `${endX - 140}px`;
      burst.style.top = `${endY - 140}px`;
      burst.style.width = '280px';
      burst.style.height = '280px';
      container.appendChild(burst);
      elements.push(burst);

      for (let i = 0; i < 3; i++) {
        const ring = document.createElement('div');
        ring.className = 'fleur-cannon-floral-ring';
        ring.style.left = `${endX - 110}px`;
        ring.style.top = `${endY - 110}px`;
        ring.style.width = '220px';
        ring.style.height = '220px';
        ring.style.animationDelay = `${i * 100}ms`;
        container.appendChild(ring);
        elements.push(ring);
      }

      for (let i = 0; i < 18; i++) {
        const petal = document.createElement('div');
        petal.className = 'fleur-cannon-petal-burst';
        const a = (i / 18) * 360;
        const r = 80 + Math.random() * 70;
        const x = endX + Math.cos(a * Math.PI / 180) * r;
        const y = endY + Math.sin(a * Math.PI / 180) * r;
        petal.style.left = `${x - 8}px`;
        petal.style.top = `${y - 8}px`;
        petal.style.animationDelay = `${i * 25}ms`;
        petal.style.setProperty('--bx', `${Math.cos(a * Math.PI / 180) * 40}px`);
        petal.style.setProperty('--by', `${Math.sin(a * Math.PI / 180) * 40}px`);
        petal.textContent = '🌸';
        container.appendChild(petal);
        elements.push(petal);
      }
    }, 1400);
    elements.push({ timer: phase3Timer, type: 'timer' });

    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('fleur-cannon-user-fire');
      playerEl.classList.add('fleur-cannon-user-recoil');

      const after = document.createElement('div');
      after.className = 'fleur-cannon-afterglow';
      after.style.left = `${startX - 80}px`;
      after.style.top = `${startY - 80}px`;
      after.style.width = '160px';
      after.style.height = '160px';
      container.appendChild(after);
      elements.push(after);
    }, 2000);
    elements.push({ timer: phase4Timer, type: 'timer' });

    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('fleur-cannon-user-charge', 'fleur-cannon-user-fire', 'fleur-cannon-user-recoil');
      opponentEl.classList.remove('fleur-cannon-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      onComplete();
    }, 2400);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('fleur-cannon-user-charge', 'fleur-cannon-user-fire', 'fleur-cannon-user-recoil');
      opponentEl.classList.remove('fleur-cannon-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

FleurCannonAnimation.moveType = 'fairy';
